import "./styling/styling.scss";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { useMutation, useSubscription } from "@apollo/client";
import React, { useCallback, useRef, useState } from "react";
import { intl } from "LocaleWrapper";
import { DossierLink, ProductLink, RelationLink, ShipLink, TerminalPlanningLink } from "utils/LinkUtils";
import NominationStatusChanger from "view/charters/components/status/NominationStatusChanger";
import SalesCharterStatusChanger from "view/charters/components/status/SalesCharterStatusChanger";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import { StatusCargoEntity } from "view/statuses/types/status";
import { useStoreState } from "ezStore";
import { Language, usePermission } from "utils";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";
import { Button, Card, PageHeader, showSnackbar } from "@cofano/cck";
import { Location, Ship } from "__generated__/graphql";
import { StatusEstimateType, StatusType, SalesCharterStatus } from "__generated__/enums";
import { ChildProps } from "types/commons";
import WithTooltip from "view/shipPlanning/components/WithTooltip";
import UpdateIcon from "@mui/icons-material/Update";
import Helmet from "components/helmet/Helmet";
import GroupedTable, { GroupedTableGroup } from "components/customCompenents/table/GroupedTable";
import UpdateShipOrderDialog from "view/shipPlanning/components/dialogs/UpdateShipOrderDialog";
import { locationDescriptionWithGroup } from "view/locations/utils/LocationUtils";
import { FormattedDate } from "utils/DateUtils";
import { useConfirm } from "components/hooks/useConfirm";
import { CargoAction } from "view/cargos/types/cargo";
import { getEstimatedStatusLabel } from "view/statuses/utils/StatusUtils";
import ShipPositionPopover from "view/ships/components/popover/ShipPositionPopover";
import CargoServiceIcons from "view/cargos/components/CargoServiceIcons";
import { AutoMode } from "@mui/icons-material";
import { ShipLinkDialog } from "view/charters/dossier/ship/ShipLinkDialog";
import UpdateShipLocationDialog from "view/shipPlanning/components/dialogs/UpdateShipLocationDialog";
import UpdateCargoEstimateTimeDialog from "view/shipPlanning/components/dialogs/UpdateCargoEstimateTimeDialog";
import UpdateStatusNoteDialog from "view/shipPlanning/components/dialogs/UpdateStatusNoteDialog";
import CargoQuantity from "view/cargos/components/quantities/CargoQuantity";
import CargoActionQuantity from "view/cargos/components/quantities/CargoActionQuantity";
import ConnectingVisitField from "view/cargos/components/connectingVisits/ConnectingVisitField";
import {
	ShipPlanningInvalidateCacheDocument,
	ShipPlanningUpdateLastContactDocument,
	UpdateActualShipPositionLocationDocument,
} from "__generated__/view/shipPlanning/graphql/shipplanning.mutations.generated";
import {
	ShipPlanningOverviewDocument,
	ShipPlanningOverviewSubscription as GeneratedShipPlanningOverviewSubscription,
} from "__generated__/view/shipPlanning/graphql/shipplanning.subscriptions.generated";
import { CallTimesFragment } from "__generated__/view/calls/graphql/call.fragments.generated";
import DeleteIcon from "@mui/icons-material/Delete";
import useConfirmDeleteVoyage from "view/voyages/hooks/useConfirmDeleteVoyage.tsx";

// Needed to omit the __typename property from a GraphQL type to prevent errors due to GraphQL types being called differently, e.g., CallShipPlanningDTO vs. Call
type OmitDistributive<T, K extends PropertyKey,> = T extends object ? OmitRecursively<T, K> : T;
type OmitRecursively<T, K extends PropertyKey,> = Omit<{ [P in keyof T]: OmitDistributive<T[P], K> }, K>

type ShipPlanningOverviewSubscription = OmitRecursively<GeneratedShipPlanningOverviewSubscription, "__typename">
type ShipPlanningRow = ChildProps<ShipPlanningOverviewSubscription, "shipPlanning">;
export type ShipType = ShipPlanningRow["ship"];

const ShipPlanningOverview = () => {

	const confirm = useConfirm();
	const [ data, setData ] = useState<ShipPlanningRow[]>([]);
	const [ updateShipLastContact ] = useMutation(ShipPlanningUpdateLastContactDocument);
	const [ updateShipLocation, { loading: shipLocationMutationLoading } ] = useMutation(UpdateActualShipPositionLocationDocument);
	const [ invalidateCache ] = useMutation(ShipPlanningInvalidateCacheDocument);

	const [ shipLinkDialogCharter, setShipLinkDialogCharter ] = useState<ShipPlanningRow["salesCharters"][number] | null>(null);

	const session = useStoreState(store => store.session);
	const { isSkipperOrShipManager } = usePermission();

	const [ sequenceDialogOpen, openSequenceDialog ] = useState<boolean>(false);

	const [ selectedShip, setSelectedShip ] = useState<ShipType | undefined>(undefined);
	const [ shipLocationDialogOpen, setShipLocationDialogOpen ] = useState(false);

	const [ cargoEstimateDialog, setCargoEstimateDialog ] = useState<{ open: boolean; id: number; shipId: number; estimateTime?: number; estimateType?: StatusEstimateType | null | undefined } | null>(null);
	const [ statusNoteDialog, setStatusNoteDialog ] = useState<{ open: boolean; statusChangeId: number; shipId: number; comment?: string, skipperComment?: string } | null>(null);

	const findShipIdForCargo = (cargoId: number): number | undefined =>
		data.find(s => s.salesCharters.some(c => c.cargos.some(cargo => cargo.id === cargoId)))?.ship.id;

	const findShipIdForStatusChange = (statusChangeId: number): number | undefined =>
		data.find(s => s.salesCharters.some(c => c.cargos.some(cargo => cargo.lastStatusChange?.id === statusChangeId)))?.ship.id;

	const openCargoEstimateDialog = (cargoId: number, estimateTime: number | undefined, estimateType: StatusEstimateType | null | undefined) => {
		const shipId = findShipIdForCargo(cargoId);
		if (shipId == null) {
			return;
		}
		setCargoEstimateDialog({ open: true, id: cargoId, shipId, estimateTime, estimateType });
	};

	const closeCargoEstimateDialog = () => {
		setCargoEstimateDialog(null);
	};

	const openStatusNoteDialog = (statusChangeId: number, comment?: string, skipperComment?: string) => {
		const shipId = findShipIdForStatusChange(statusChangeId);
		if (shipId == null) {
			return;
		}
		setStatusNoteDialog({ open: true, statusChangeId, shipId, comment, skipperComment });
	};

	const closeStatusNoteDialog = () => {
		setStatusNoteDialog(null);
	};

	const openShipLocationDialog = (ship: ShipType) => {
		setSelectedShip(ship);
		setShipLocationDialogOpen(true);
	};

	const closeShipLocationDialog = () => {
		setSelectedShip(undefined);
		setShipLocationDialogOpen(false);
	};

	const refetch = useCallback(() => { }, []);

	const { loading: unplanLoading, confirmDeleteVoyage } = useConfirmDeleteVoyage({
		refetch: () => {
			refetch();
			showSnackbar(intl.formatMessage({ id: "VOYAGE_SUCCESSFULLY_DELETED" }), { variant: "success" });
		},
	});

	type CargoRow = ShipPlanningRow["salesCharters"][number]["cargos"][number];
	const groupCache = useRef(new Map<number, { data: ShipPlanningRow; group: GroupedTableGroup<CargoRow> }>());

	const buildGroup = (shipWithCharter: ShipPlanningRow): GroupedTableGroup<CargoRow> => ({
		id: shipWithCharter.ship.id,
		backgroundColor: "#D6E4F1",
		render: () => {
			return (
				<>
					<span style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<ConnectingShipTooltip ship={shipWithCharter.ship} />
						<IconButton
							size='small'
							onClick={() => saveShipLastContact(shipWithCharter.ship)}
						>
							<UpdateIcon fontSize="small" />
						</IconButton>
					</span>
					<span style={{ display: "flex", alignItems: "center", justifyContent: shipWithCharter.ship.lastContact ? "space-between" : "end", gap: 8 }}>
						<FormattedDate date={shipWithCharter.ship.lastContact} format="lll" withoutYear />
					</span>
					<ShipLocation ship={shipWithCharter.ship} onClick={() => openShipLocationDialog(shipWithCharter.ship)} />
				</>
			);
		},
		subGroups: shipWithCharter.salesCharters
		// sort on the first loading action date
			.sort((a, b) => {
				const getEarliestLoadingDate = (charterWithCargoes: typeof a) => {
					const dates = charterWithCargoes.cargos
						.map(cargo => cargo.firstLoadingAction?.start)
						.filter(start => start !== null && start !== undefined);
					return dates.length > 0 ? dates.sort()[0] : null;
				};

				const dateA = getEarliestLoadingDate(a);
				const dateB = getEarliestLoadingDate(b);

				if (!dateA && !dateB) {
					return 0;
				}
				if (!dateA) {
					return 1;
				}
				if (!dateB) {
					return -1;
				}

				return new Date(dateA).getTime() - new Date(dateB).getTime();
			})
			.map(charter => ({
				id: charter.id,
				backgroundColor: "#eee",
				render: () => {
					return (
						<div style={{ display: "flex", gap: "12px", justifyContent: "space-between", alignItems: "center" }}>
							<div style={{ display: "flex", gap: "12px", alignItems: "center", flex: 1, minWidth: 0 }}>
								<div style={{ minWidth: 100, flexShrink: 0 }}>
									<DossierLink charter={charter} />
								</div>

								<div style={{ width: 140, flexShrink: 0 }}>
									<Tooltip title={charter.broker?.name ?? charter.debtor?.name} slotProps={{ tooltip: { sx: { whiteSpace: "pre-wrap" } } }}>
										<span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><RelationLink relation={charter.broker ?? charter.debtor} /></span>
									</Tooltip>
								</div>

								<div style={{ minWidth: 80, flexShrink: 0 }}>
									<SalesCharterStatusChanger
										charter={charter}
										onCompleted={refetch}
									/>
								</div>

								{(charter.lastShipNominationStatus?.status && charter.voyages?.[0] && (
									<div style={{ minWidth: 80, flexShrink: 0 }}>
										<NominationStatusChanger
											charter={charter}
											refetch={refetch}
										/>
									</div>
								))}

								{charter.voyages?.map(voyage => (
									<div key={voyage.id} style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
										<StatusChanger
											session={session}
											// @ts-expect-error handwritten vs generated
											entity={voyage}
											statusType={StatusType.Voyage}
											refetch={refetch}
											showHistory={true}
										/>
									</div>
								))}

								<Tooltip title={charter.internalNotes} slotProps={{ tooltip: { sx: { whiteSpace: "pre-wrap" } } }}>
									<span style={{ fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
										{charter.internalNotes}
									</span>
								</Tooltip>
							</div>
							{charter.status.name === SalesCharterStatus.Planned && (
								<Box display="flex" alignItems="center" gap={1}>
									<Tooltip title={intl.formatMessage({ id: "RELINK_SHIP" })}>
										<IconButton
											size="small"
											className="ship-planning-btn"
											onClick={() => setShipLinkDialogCharter(charter)}
										>
											<AutoMode fontSize="small" />
										</IconButton>
									</Tooltip>
									<Tooltip title={intl.formatMessage({ id: "DELETE_VOYAGE" })}>
										<IconButton
											size="small"
											className="ship-planning-btn"
											disabled={unplanLoading}
											onClick={() => confirmDeleteVoyage({
												charterId: charter.id,
												voyageId: charter.voyages[0].id,
												cargoIds: charter.cargos.map(cargo => cargo.id),
											})}
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</Box>
							)}
						</div>
					);
				},
				rows: charter.cargos,
			})),
	});

	const groups = data
		.filter(s => isSkipperOrShipManager ? session.user?.properties.shipIds?.includes(s.ship.id) : true)
		.filter(s => s.ship.operator?.isApplicationRelation || s.salesCharters.length > 0)
		.sort((a, b) => {
			const seqA = a.ship.sequenceNumber;
			const seqB = b.ship.sequenceNumber;
			if (seqA != null && seqB != null) {
				return seqA - seqB;
			}
			if (seqA != null) {
				return -1;
			}
			if (seqB != null) {
				return 1;
			}
			return a.ship.name.localeCompare(b.ship.name);
		})
		.map(shipWithCharter => {
			const cached = groupCache.current.get(shipWithCharter.ship.id);
			if (cached && cached.data === shipWithCharter) {
				return cached.group;
			}
			const group = buildGroup(shipWithCharter);
			groupCache.current.set(shipWithCharter.ship.id, { data: shipWithCharter, group });
			return group;
		});

	// clear up cache entries for ships no longer in data
	const currentShipIds = new Set(data.map(s => s.ship.id));
	for (const key of groupCache.current.keys()) {
		if (!currentShipIds.has(key)) {
			groupCache.current.delete(key);
		}
	}

	const dataBuffer = useRef<ShipPlanningRow[]>([]);
	const rafId = useRef<number>();

	useSubscription<ShipPlanningOverviewSubscription>(ShipPlanningOverviewDocument, {
		onData: (patchResponse) => {
			const patch = patchResponse.data.data!.shipPlanning;

			const shipIndex = dataBuffer.current.findIndex(s => s.ship.id === patch.ship.id);
			if (shipIndex >= 0) {
				dataBuffer.current[shipIndex] = patch;
			} else {
				dataBuffer.current.push(patch);
			}

			if (!rafId.current) {
				rafId.current = requestAnimationFrame(() => {
					setData([ ...dataBuffer.current ]);
					rafId.current = undefined;
				});
			}
		},
	});

	const saveShipLastContact = (ship: ShipPlanningRow["ship"]) => {
		confirm({
			type: "WARNING",
			title: intl.formatMessage({ id: "CONFIRM_UPDATE_SHIP_LAST_CONTACT" }),
			confirmText: intl.formatMessage({ id: "YES" }),
		}).then(async () => {
			await updateShipLastContact({
				variables: {
					id: ship.id,
				},
			});
			await invalidateCache({ variables: { shipId: ship.id } });
		});
	};

	const saveShipLocation = (location?: string) => {
		if (selectedShip?.id == null) {
			return;
		}

		updateShipLocation({
			variables: {
				input: {
					shipId: selectedShip.id,
					location: location,
				},
			},
		}).then(async () => {
			await invalidateCache({ variables: { shipId: selectedShip.id } });
		}).finally(() => {
			closeShipLocationDialog();
		});
	};

	return <div className="ship-planning">
		<PageHeader>
			<PageHeader.Title>
				{Language("SHIP_PLANNING")}
				<Helmet title={intl.formatMessage({ id: "SHIP_PLANNING" })} />
			</PageHeader.Title>
			<PageHeader.Actions>
				{data.length === 0 && (
					<div style={{ display: "flex", alignItems: "center", height: 34, paddingRight: 16 }}>
						{<CircularProgress size={20} />}
					</div>
				)}
				<Button
					disabled={data.length === 0}
					onClick={() => openSequenceDialog(true)}
					variant="outlined">
					{Language("CHANGE_ORDER")}
				</Button>
			</PageHeader.Actions>
		</PageHeader>

		<UpdateShipOrderDialog
			open={sequenceDialogOpen}
			onClose={() => openSequenceDialog(false)}
			refetch={invalidateCache}
		/>

		<Card disablePadding>
			<GroupedTable
				headerInFront
				size="compact"
				spacingBetweenGroups={24}
				groups={groups}
				paginationSettings={{ paginationEnabled: false }}
				contentLoading={groups.length === 0}
				getEntityId={row => row.id}
				columns={[
					{
						id: "ship",
						title: intl.formatMessage({ id: "SHIP" }),
						width: 170,
					}, {
						id: "cargo",
						title: intl.formatMessage({ id: "CARGO" }),
						maxWidth: 150,
						render: (row) => <span style={{ display: "flex", gap: 12 }}>{row.cargoNumber} <ProductLink product={row.product} /></span>,
					}, {
						id: "quantity",
						title: intl.formatMessage({ id: "QUANTITY" }),
						render: (row) => {
							const hasActuals = row?.firstLoadingAction?.measurements.locationWeight
									|| row?.firstLoadingAction?.measurements?.locationVolume
									|| row?.firstLoadingAction?.measurements?.skipperWeight
									|| row?.firstLoadingAction?.measurements?.skipperVolume;

							return <React.Fragment>
								{hasActuals && row.firstLoadingAction ? (
									<CargoActionQuantity
										cargoAction={row.firstLoadingAction}
										cargo={row}
										convertActualsToMton
										fallbackToActuals
										grow
									/>
								) : (
									<CargoQuantity cargo={row} grow />
								)}
							</React.Fragment>;
						},
					}, {
						id: "services",
						title: intl.formatMessage({ id: "CARGO_SERVICES" }),
						render: (item) => <CargoServiceIcons cargo={item} />,
					}, {
						id: "loading",
						title: intl.formatMessage({ id: "LOADING" }),
						minWidth: 250,
						maxWidth: "unset",
						render: (row) => row.firstLoadingAction && (
							<div className="action-info">
								<span style={{ minWidth: 45 }}><CargoActionDate action={row.firstLoadingAction} withoutYear hideIfSimulated /></span>
								<span style={{ minWidth: 60 }}><LocationTooltip action={row.firstLoadingAction} /></span>
								<ConnectingVisitField action={row.firstLoadingAction} />
							</div>
						),
					}, {
						id: "discharging",
						title: intl.formatMessage({ id: "DISCHARGING" }),
						minWidth: 250,
						maxWidth: "unset",
						render: (row) => row.lastDischargingAction && (
							<div className="action-info">
								<span style={{ minWidth: 45 }}><CargoActionDate action={row.lastDischargingAction} withoutYear hideIfSimulated /></span>
								<span style={{ minWidth: 60 }}><LocationTooltip action={row.lastDischargingAction} /></span>
								<ConnectingVisitField action={row.lastDischargingAction} />
							</div>
						),
					}, {
						id: "status",
						title: intl.formatMessage({ id: "STATUS" }),
						width: 130,
						render: (row) => (
							<StatusChanger
								session={session}
								entity={row as unknown as StatusCargoEntity}
								statusType={StatusType.Cargo}
								refetch={refetch}
								showHistory={true}
							/>
						),
					}, {
						id: "estimation",
						title: intl.formatMessage({ id: "ESTIMATED" }),
						width: 100,
						render: (row) => (
							<span>
								{row.lastStatusChange?.callEstimateTime ? (
									<Tooltip title={getEstimatedStatusLabel(row.lastStatusChange.status.estimateType)}>
										<span
											style={{ cursor: "pointer" }}
											onClick={() => openCargoEstimateDialog(
												row.id,
												row.lastStatusChange?.callEstimateTime ? new Date(row.lastStatusChange.callEstimateTime).getTime() : undefined,
												row.lastStatusChange?.status.estimateType,
											)}
										>
											<FormattedDate
												date={row.lastStatusChange.callEstimateTime}
												withoutYear
											/>
											{row.lastStatusChange.status.estimateType && (
												<sub><small>{intl.formatMessage({ id: `STATUS_${row.lastStatusChange.status.estimateType.toString()}` })}</small></sub>
											)}
										</span>
									</Tooltip>
								) : row.lastStatusChange?.status.estimateType !== StatusEstimateType.StatusChange && !!row.lastStatusChange?.status.estimateType && (
									<div
										className="note-placeholder"
										style={{ cursor: "pointer" }}
										onClick={() => openCargoEstimateDialog(
											row.id,
											undefined,
											row.lastStatusChange?.status.estimateType)}
									></div>
								)}
							</span>
						),
					}, {
						id: "comment",
						title: intl.formatMessage({ id: "NOTE" }),
						minWidth: 150,
						render: (row) => (
							row.lastStatusChange ? (
								row.lastStatusChange.comment ?? row.lastStatusChange.skipperComment ? (
									<Tooltip title={row.lastStatusChange.comment ?? row.lastStatusChange.skipperComment} slotProps={{ tooltip: { sx: { whiteSpace: "pre-wrap" } } }}>
										<span
											style={{ overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer" }}
											onClick={() => openStatusNoteDialog(row.lastStatusChange!.id, row.lastStatusChange!.comment ?? undefined, row.lastStatusChange!.skipperComment ?? undefined)}
										>
											<span style={{ fontStyle: row.lastStatusChange.comment ? "inherit" : "italic" }}>
												{row.lastStatusChange.comment ?? row.lastStatusChange.skipperComment}
											</span>
										</span>
									</Tooltip>
								) : (
									<div
										className="note-placeholder"
										style={{ cursor: "pointer" }}
										onClick={() => openStatusNoteDialog(row.lastStatusChange!.id, undefined, undefined)}
									></div>
								)
							) : null
						),
					} ]} />
		</Card>

		{shipLinkDialogCharter && (
			<ShipLinkDialog
				charter={shipLinkDialogCharter}
				existingVoyage={shipLinkDialogCharter.voyages?.[0]}
				open={!!shipLinkDialogCharter}
				onClose={() => setShipLinkDialogCharter(null)}
				onCompleted={() => {
					showSnackbar(intl.formatMessage({ id: "SHIP_SUCCESSFULLY_LINKED" }), { variant: "success" });
				}}
				resetShipNomination
			/>
		)}

		{selectedShip && (
			<UpdateShipLocationDialog
				ship={selectedShip}
				open={shipLocationDialogOpen}
				loading={shipLocationMutationLoading}
				onClose={closeShipLocationDialog}
				onUpdate={(location?: string) => {
					saveShipLocation(location);
				}} />
		)}

		{cargoEstimateDialog && (
			<UpdateCargoEstimateTimeDialog
				cargoId={cargoEstimateDialog.id}
				initialValue={cargoEstimateDialog.estimateTime}
				open={cargoEstimateDialog.open}
				estimateType={cargoEstimateDialog.estimateType}
				onClose={closeCargoEstimateDialog}
				onSuccess={() => invalidateCache({ variables: { shipId: cargoEstimateDialog.shipId } })}
			/>
		)}

		{statusNoteDialog && (
			<UpdateStatusNoteDialog
				statusChangeId={statusNoteDialog.statusChangeId}
				comment={statusNoteDialog.comment}
				skipperComment={statusNoteDialog.skipperComment}
				open={statusNoteDialog.open}
				onClose={closeStatusNoteDialog}
				onSuccess={() => invalidateCache({ variables: { shipId: statusNoteDialog.shipId } })}
			/>
		)}
	</div>;
};

type LocationTooltipProps = {
	action: Pick<CargoAction, "terminalPlanningNotes"> & {
		location?: Pick<Location, "id" | "description" | "lastTerminalContactDate"> & {
			locationGroups?: Pick<Location, "description" | "type">[] | null
		} | null
	}
};

const LocationTooltip = ({ action }: LocationTooltipProps) => {
	const showNotesTooltip = !!action.terminalPlanningNotes;

	if (!action.location) {
		return null;
	}

	const locationDescription = locationDescriptionWithGroup(action.location);

	if (showNotesTooltip) {
		return <WithTooltip
			title={
				<>
					<span>{locationDescription}</span>
					<Box mt={1}>
						<FormattedDate date={action.location.lastTerminalContactDate} format="lll" withoutYear />
						<Box>{action.terminalPlanningNotes}</Box>
					</Box>
				</>
			}>
			<TerminalPlanningLink location={action.location} />
		</WithTooltip>;
	} else {
		return <WithTooltip
			title={locationDescription}
			textDecoration={"none"}>
			<TerminalPlanningLink location={action.location} />
		</WithTooltip>;
	}
};

const ConnectingShipTooltip = ({ ship }: { ship: Pick<Ship, "id" | "name" | "planningNotes"> }) => {
	const hasShipPlanningNotes = ship.planningNotes?.trim() ? ship.planningNotes?.trim().length > 0 : false;

	return (
		<b>
			<WithTooltip
				textDecoration={hasShipPlanningNotes ? "underline" : "none"}
				title={(
					<>
						<span style={{ fontWeight: "bold" }}>{ship.name}</span>
						<Box>{ship.planningNotes}</Box>
					</>
				)}
			>
				<div style={{ display: "flex", justifyContent: "space-between" }}>
					<ShipLink ship={ship} />
				</div>
			</WithTooltip>
		</b>
	);
};

const ShipLocation = ({ ship, onClick }: { ship: Pick<Ship, "id" | "name"> & { nextCall?: Omit<CallTimesFragment, "__typename"> | null } & { position?: Omit<NonNullable<Ship["position"]>, "__typename"> | null }; onClick: () => void }) => {
	const location = ship.position?.location ?? undefined;

	const isMultiline = location != null && location.includes(",");

	const text = location
		? location.split(",").map(x => x.trim()).join("\n")
		: intl.formatMessage({ id: "UNKNOWN_LOCATION" });

	return (
		<Box sx={{ display: "flex", marginTop: 1, justifyContent: "space-between", alignItems: isMultiline ? "baseline" : "center" }}>
			<Tooltip title={ship.position?.locationEntered ? <FormattedDate date={ship.position.locationEntered} format="lll" withoutYear /> : ""}>
				<Typography onClick={onClick} component="span" className="ship-location">
					{text}
				</Typography>
			</Tooltip>

			<ShipPositionPopover ship={ship} />
		</Box>
	);
};

export default ShipPlanningOverview;
